import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import BenchmarkPage from './pages/BenchmarkPage';
import DashboardPage from './pages/DashboardPage';
import SearchPage from './pages/SearchPage';
import MoleculeDetailPage from './pages/MoleculeDetailPage';
import ComparisonPage from './pages/ComparisonPage';
import GeographyPage from './pages/GeographyPage';
import SimulatorPage from './pages/SimulatorPage';
import AlertsPage from './pages/AlertsPage';
import ReportsPage from './pages/ReportsPage';
import AdminPage from './pages/AdminPage';
import AdminConsolePage from './pages/AdminConsolePage';

// New Infrastructure Pages
import CFODashboardPage from './pages/CFODashboardPage';
import PharmacyDashboardPage from './pages/PharmacyDashboardPage';
import PipelinePage from './pages/PipelinePage';
import SavingsLedgerPage from './pages/SavingsLedgerPage';
import ActionCenterPage from './pages/ActionCenterPage';
import InterventionsPage from './pages/InterventionsPage';
import FormularyPage from './pages/FormularyPage';
import TimelinePage from './pages/TimelinePage';
import SourceHealthPage from './pages/SourceHealthPage';
import PeerIntelligencePage from './pages/PeerIntelligencePage';

// Components
import Layout from './components/Layout';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-slate-200 border-t-[#0A2540] rounded-full animate-spin mx-auto" />
          <p className="text-slate-500 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-slate-200 border-t-[#0A2540] rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public routes — accessible without login */}
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <BenchmarkPage />} />
      <Route path="/benchmark" element={user ? <Navigate to="/dashboard" replace /> : <BenchmarkPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      <Route path="/drug/:drugId" element={<ProtectedRoute><MoleculeDetailPage /></ProtectedRoute>} />
      <Route path="/compare/:drugId" element={<ProtectedRoute><ComparisonPage /></ProtectedRoute>} />
      <Route path="/geography" element={<ProtectedRoute><GeographyPage /></ProtectedRoute>} />
      <Route path="/simulator" element={<ProtectedRoute><SimulatorPage /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminConsolePage /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      
      {/* New Infrastructure Routes */}
      <Route path="/cfo-dashboard" element={<ProtectedRoute><CFODashboardPage /></ProtectedRoute>} />
      <Route path="/pharmacy-dashboard" element={<ProtectedRoute><PharmacyDashboardPage /></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute><PipelinePage /></ProtectedRoute>} />
      <Route path="/savings" element={<ProtectedRoute><SavingsLedgerPage /></ProtectedRoute>} />
      <Route path="/action-center" element={<ProtectedRoute><ActionCenterPage /></ProtectedRoute>} />
      <Route path="/interventions" element={<ProtectedRoute><InterventionsPage /></ProtectedRoute>} />
      <Route path="/formulary" element={<ProtectedRoute><FormularyPage /></ProtectedRoute>} />
      <Route path="/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
      <Route path="/source-health" element={<ProtectedRoute><SourceHealthPage /></ProtectedRoute>} />
      <Route path="/peer-intelligence" element={<ProtectedRoute><PeerIntelligencePage /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
